import { cn } from './cn.js';
import { Icon } from './Icon.js';
import type { Photo } from './SummaryStrip.js';

/**
 * A foto grande, com anterior e próxima, a legenda com "2 de 5" e a faixa de
 * miniaturas embaixo.
 *
 * Mora dentro da `SideDrawer`, e não no visualizador do `AttachmentGallery`:
 * aquele abre por cima de tudo, e a gaveta existe para a foto ficar ao lado do
 * formulário enquanto ele é preenchido.
 *
 * Quem guarda o índice é quem chama (`index` + `onIndexChange`): a miniatura
 * do cabeçalho abre a gaveta numa foto específica, e o índice precisa
 * sobreviver a fechar e abrir.
 *
 * **Sem atalho de teclado** — a decisão da mesa (item 16 do README do
 * sinistro). Seta esquerda e direita são do campo que estiver em foco.
 */
export function PhotoViewer({
  photos,
  index,
  onIndexChange,
}: {
  photos: readonly Photo[];
  index: number;
  onIndexChange: (index: number) => void;
}) {
  const at = Math.min(Math.max(index, 0), photos.length - 1);
  const photo = photos[at];
  if (!photo) return null;
  const many = photos.length > 1;
  return (
    <div className="flex flex-col gap-2">
      <div
        data-chrome
        className="relative aspect-[4/3] overflow-hidden rounded-borda bg-chrome"
      >
        <img src={photo.src} alt={photo.alt} className="size-full object-contain" />
        {many && (
          <>
            <ArrowButton
              side="left"
              label="Foto anterior"
              disabled={at === 0}
              onClick={() => onIndexChange(at - 1)}
            />
            <ArrowButton
              side="right"
              label="Próxima foto"
              disabled={at === photos.length - 1}
              onClick={() => onIndexChange(at + 1)}
            />
          </>
        )}
      </div>
      <div className="flex justify-between gap-2.5 text-sm">
        <span className="min-w-0 font-semibold break-words">
          {photo.caption ?? photo.alt}
        </span>
        <span className="shrink-0 text-texto-suave tabular-nums">
          {at + 1} de {photos.length}
        </span>
      </div>
      {many && (
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {photos.map((p, i) => (
            <button
              key={`${p.src}-${i}`}
              type="button"
              aria-label={`Foto ${i + 1}: ${p.alt}`}
              aria-current={i === at || undefined}
              onClick={() => onIndexChange(i)}
              className={cn(
                'h-11 w-14 shrink-0 overflow-hidden rounded-borda bg-borda',
                i === at
                  ? 'border-2 border-primaria'
                  : 'border border-borda opacity-70 hover:opacity-100',
              )}
            >
              <img src={p.src} alt="" className="size-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ArrowButton({
  side,
  label,
  disabled,
  onClick,
}: {
  side: 'left' | 'right';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-superficie/90 text-texto active:scale-95 disabled:opacity-35',
        side === 'left' ? 'left-2' : 'right-2',
      )}
    >
      <Icon nome={side === 'left' ? 'seta-esquerda' : 'seta-direita'} size={20} />
    </button>
  );
}
